import {ChangeDetectionStrategy, Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {filter, tap} from 'rxjs/operators';
import {TitleService} from '../core/title.service';
import {CommonService} from '../main/common.service';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {AuthService} from '../core/auth.service';
import {RedirectUserService} from '../core/redirect-user.service';
import {environment} from '../../environments/environment';
// @ts-ignore
import * as CryptoJS from 'crypto-js';

const domain_urls: { [key: string]: string } = environment?.aiLiveDomainUrls


interface UserInfo {
    first_name: string;
    last_name: string;
    email: string;
}


@Component({
    selector: 'sam4-viewer',
    templateUrl: './viewer.component.html',
    styleUrls: ['./viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ViewerComponent implements OnInit {
    listOfUsers: any;
    guid: any;
    ai_live_id: any;
    emergency_session_link: any;
    param: any;
    mapURL: any = '';
    safeUrl!: SafeResourceUrl;
    wakeLock = null;

    constructor(private router: Router, private route: ActivatedRoute, private title: TitleService, private common: CommonService, private sanitizer: DomSanitizer,
                private auth: AuthService,
                private redirectService: RedirectUserService) {
        this.title.set('Caption Viewer');
    }

    @HostListener('document:visibilitychange')
    visibilitychange() {
        this.checkHiddenDocument();
    }

    checkHiddenDocument() {
        if (document.hidden) {
            console.log('Wake lock is deactivated.');
        } else {
            this.requestWakeLock();
        }
    }

    private typeChecker(listOfUsers: string, thisUser: string) {
        if (listOfUsers) {
            try {
                // Check if the domains instead of a full email address
                const checkDomains = listOfUsers.indexOf('@');
                if (checkDomains === -1) {
                    const at_symbol = thisUser.indexOf('@');
                    const userDomain = thisUser.substr(at_symbol + 1);
                    const arrDomain = this.listOfUsers.split(',').map((domain: string) => {
                        // Normalize domain by removing protocol prefixes and www.
                        return domain.trim()
                            .replace(/^https?:\/\//i, '')  // Remove http:// or https://
                            .replace(/^www\./i, '')          // Remove www.
                            .toLowerCase();
                    });
                    const normalizedUserDomain = userDomain.toLowerCase();
                    if (arrDomain.includes(normalizedUserDomain)) {
                        return true;
                    }
                }
                const arrEmail = this.listOfUsers.split(',');
                if (arrEmail.includes(thisUser)) {
                    return true;
                }
            } catch {
                return false;
            }
            return false;
        } else {
            return false;
        }
    }


    requestWakeLock() {
        if ('wakeLock' in navigator) {
            try {
                const anyNav: any = navigator;
                // istanbul ignore else -- unreachable: the enclosing `if` already asserted the
                // identical condition and nothing between the two mutates `navigator`.
                if ('wakeLock' in navigator) {
                    this.wakeLock = anyNav['wakeLock'].request('screen');
                }
                console.log('Wake lock is activated.');
            } catch (err) {
                console.log(err);
            }
        }
    }

    handleVisibilityChange = async () => {
        if (this.wakeLock !== null && document.visibilityState === 'visible') {
            await this.requestWakeLock();
        }
    }


    getAiLiveCaptionUrlParams(viewer_settings: { [key: string]: any }) {
        return '&' + Object
            .entries(viewer_settings)
            .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
            .join('&')
    }

    ngOnInit(): void {


        this.route.queryParams.pipe(filter(params => params.id)).subscribe(val => this.param = val.id);


        this.common.getViewerUsers(this.param).pipe(tap(val => {
            this.ai_live_id = val.ai_live_id;
            this.listOfUsers = val.allowed_viewer;
            this.emergency_session_link = val.emergency_session_link || '';
        })).subscribe(value => {


            this.auth.url = '/viewer/event?id=' + this.param;
            this.auth.listOfUsers = this.listOfUsers;

            const captionUrlParams = this.getAiLiveCaptionUrlParams(value?.viewer_settings)

            const region = value.creator_location || 'us';

            this.mapURL = `${domain_urls[region] || domain_urls['us']}${value.ai_live_id}${captionUrlParams}`;

            if (this.emergency_session_link) {
                const isDemo = this.emergency_session_link?.includes('/Viewer/Demo')
                this.mapURL = this.emergency_session_link;
                if (this.emergency_session_link.includes('ai-live.com') && !isDemo) {
                    this.mapURL = `${this.emergency_session_link}${captionUrlParams}` ;
                }
            }

            const iframe = document.getElementById('iframeTest');
            const localCryEmail = localStorage.getItem('app_state') || '';
            const decrypted = CryptoJS.AES.decrypt(localCryEmail, 'hvjABU0adw_xjw0eyksdvsdvsdvhvjABU0adw_xjwk');
            const mode: string | null = sessionStorage.getItem('app_user_info') || '';
            const localEmail: string | null = decrypted.toString(CryptoJS.enc.Utf8) || '';

            if (mode) {
                const emailKey: UserInfo = JSON.parse(mode);
                if (this.typeChecker(this.listOfUsers, emailKey.email)) {
                    localStorage.removeItem('bookingsRedirectLoginItem');
                    if (iframe) {
                        iframe.setAttribute('src', this.mapURL);
                    }
                } else {
                    this.router.navigate(['/notauthorized']);
                }
            } else if (localEmail) {
                if (this.typeChecker(this.listOfUsers, localEmail)) {
                    localStorage.removeItem('bookingsRedirectLoginItem');
                    if (iframe) {
                        iframe.setAttribute('src', this.mapURL);
                    }
                } else {
                    this.router.navigate(['/notauthorized']);
                }
            } else if (this.listOfUsers === 'NOT_ACTIVE_YET') {
                // No in-window BookingSession row was found: this is a timing state,
                // not an auth outcome, so show it directly without invoking
                // auth.process() (which would self-navigate and race — see LBP-317).
                this.router.navigate(['viewer-not-activated']);
            } else if (this.auth.process()) {
                localStorage.removeItem('bookingsRedirectLoginItem');
                if (iframe) {
                    iframe.setAttribute('src', this.mapURL);
                }
            }
            // When auth.process() returns false it has ALREADY navigated:
            //   not authenticated -> redirectToLogin()        -> /bookings (return url saved)
            //   authed & unlisted -> redirectToNotAuthorized() -> /notauthorized
            // (this includes empty allowed_viewer). Do not add another branch
            // here — a second navigate() would cancel process()'s navigation.

        });
    }


    // tslint:disable-next-line:use-lifecycle-interface
    ngAfterViewInit() {
        this.requestWakeLock();

    }

}
